<template>
    <TprmModal
        open
        title="Add a New Note"
        subtitle="Add a note to this entity's activity history."
        size="md"
        :allow-close="!submitting"
        @close="$emit('close')"
    >
        <div class="AddNoteModal">
            <div class="AddNoteModal__noteField">
                <TextareaInput
                    id="addNoteModal__note"
                    label="Note"
                    placeholder="Enter your note..."
                    required
                    :value="note"
                    :disabled="submitting"
                    :error="noteError"
                    :rows="4"
                    :maxlength="maxNoteLength"
                    @input="onNoteInput"
                />
                <div
                    v-if="isAtMaxLength"
                    class="AddNoteModal__charLimit"
                >
                    Maximum {{ maxNoteLength }} characters reached.
                </div>
            </div>
            <TagSelector
                :rp-name="rpName"
                resource-type="NOTE"
                :value="tags"
                :disabled="submitting"
                @input="tags = $event"
            />
            <div v-if="error" class="AddNoteModal__errorBanner">
                {{ error }}
            </div>
        </div>
        <template #footer>
            <NewButton
                variant="outline"
                color="neutral"
                :disabled="submitting"
                @click="$emit('close')"
            >
                Cancel
            </NewButton>
            <NewButton
                variant="fill"
                color="primary"
                :loading="submitting"
                :disabled="submitting"
                @click="onSubmit"
            >
                Add Note
            </NewButton>
        </template>
    </TprmModal>
</template>

<style lang="scss">
.AddNoteModal {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &__charLimit {
        margin-top: 4px;
        font-size: 12px;
        color: var(--eidDangerRed);
    }

    &__errorBanner {
        padding: 10px 12px;
        background-color: var(--eidDangerRed-10);
        border: 1px solid var(--eidDangerRed);
        border-radius: 6px;
        color: var(--eidDangerRed);
        font-size: 13px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import TextareaInput
        from '@/modules/dynamic-form/components/style-inputs/textarea-input/TextareaInput.vue';
    import TagSelector from '@/modules/entity-tags/components/TagSelector/TagSelector.vue';

    const MAX_NOTE_LENGTH = 1024;

    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'submit' but it is
    // neither declared in the emits option nor as an 'onClose'/'onSubmit' prop" every
    // time it fires.
    export default defineComponent({
        name: 'AddNoteModal',
        components: {
            TprmModal,
            NewButton,
            TextareaInput,
            TagSelector,
        },
        props: {
            rpName: {
                type: String as PropType<string>,
                required: true,
            },
            submitting: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            error: {
                type: String as PropType<string>,
                default: '',
            },
        },
        emits: [ 'close', 'submit' ],
        data() {
            return {
                maxNoteLength: MAX_NOTE_LENGTH,
                note: '',
                tags: [] as string[],
                submitAttempted: false,
            };
        },
        computed: {
            isAtMaxLength(): boolean {
                return this.note.length >= MAX_NOTE_LENGTH;
            },
            noteError(): string {
                if (!this.submitAttempted) {
                    return '';
                }
                if (!this.note.trim()) {
                    return 'Note is required.';
                }
                if (this.note.trim().length > MAX_NOTE_LENGTH) {
                    return `Note must be ${MAX_NOTE_LENGTH} characters or less.`;
                }
                return '';
            },
        },
        methods: {
            onNoteInput(event: Event): void {
                const target = event.target as HTMLTextAreaElement;
                this.note = target.value;
            },
            onSubmit(): void {
                this.submitAttempted = true;
                if (!this.note.trim() || this.note.trim().length > MAX_NOTE_LENGTH) {
                    return;
                }
                this.$emit('submit', { text: this.note.trim(), tags: this.tags });
            },
        },
    });
</script>
